import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Tab, Tabs } from 'react-bootstrap';
import { apiClient } from '../api';
import { integrationIntegration } from './connection-points/IntegrationsStep';
import OverviewTab from './EditGatewayPage/OverviewTab';
import PublishingTab from './EditGatewayPage/PublishingTab';
import RemoteTab from './EditGatewayPage/RemoteTab';
import GatewayIntegrationsTab from './EditGatewayPage/GatewayIntegrationsTab';

interface GatewayForm {
  name: string;
  description: string;
  did: string;
  gateway_type: 'self' | 'remote';
  status: 'active' | 'disabled';
  exposed_channels?: string[];
  integrations?: integrationIntegration[];
}

interface RemoteChannel {
  config_id: string;
  name: string;
  description?: string;
  listen_address: string;
  protocol: string;
}

const EditGatewayPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = id !== undefined && id !== 'new';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<GatewayForm>({
    name: '',
    description: '',
    did: '',
    gateway_type: 'remote',
    status: 'active',
  });
  const [error, setError] = useState<string>('');
  const [remoteChannels, setRemoteChannels] = useState<RemoteChannel[]>([]);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [channelsError, setChannelsError] = useState<string>('');
  const [showChannels, setShowChannels] = useState(false);
  const [allChannels, setAllChannels] = useState<any[]>([]);
  const [savingExposedChannels, setSavingExposedChannels] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const isSelfGateway = form.gateway_type === 'self';
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [hasIntegrationErrors, setHasIntegrationErrors] = useState(false);
  const [integrationsModified, setIntegrationsModified] = useState(false);
  const [savingIntegrations, setSavingIntegrations] = useState(false);

  // Handle /gateways/self - look up the actual self gateway ID and redirect
  useEffect(() => {
    if (id === 'self') {
      const resolveSelfGateway = async () => {
        try {
          const response = await apiClient.get('/gateways');
          const selfGateway = response.data.find((g: any) => g.gateway_type === 'self');
          if (selfGateway) {
            navigate(`/gateways/${encodeURIComponent(selfGateway.id)}`, { replace: true });
          } else {
            setError('No self gateway found');
          }
        } catch (error: any) {
          setError(error.message || 'Failed to resolve self gateway');
        }
      };
      resolveSelfGateway();
    }
  }, [id, navigate]);

  useEffect(() => {
    if (isEditMode && id && id !== 'self') {
      fetchGateway();
    }
  }, [id, isEditMode]);

  const fetchGateway = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/gateways/${id}`);
      setForm({
        name: response.data.name,
        description: response.data.description,
        did: response.data.did,
        gateway_type: response.data.gateway_type,
        integrations: [],
        status: response.data.status,
        exposed_channels: response.data.exposed_channels || [],
      });
      
      // Load integrations from separate endpoint
      try {
        await fetchGatewayIntegrations();
      } catch (err) {
        console.error('Error in fetchGatewayIntegrations call:', err);
      }
      
      // Load all local channels for remote gateways
      if (response.data.gateway_type === 'remote') {
        fetchAllChannels();
      }
      
      // Auto-load channels for remote gateways
      if (response.data.gateway_type === 'remote' && response.data.status === 'active') {
        setShowChannels(true);
        fetchRemoteChannels();
      }
    } catch (error: any) {
      setError(error.message || 'Failed to load gateway');
    } finally {
      setLoading(false);
    }
  };

  const fetchRemoteChannels = async (forceRefresh: boolean = false) => {
    if (!id) return;
    
    try {
      setLoadingChannels(true);
      setChannelsError('');
      const url = forceRefresh 
        ? `/gateways/${id}/channels?force_refresh=true`
        : `/gateways/${id}/channels`;
      const response = await apiClient.get(url);
      
      if (response.data.success) {
        setRemoteChannels(response.data.channels || []);
      } else {
        setChannelsError(response.data.message || 'Failed to fetch channels');
      }
    } catch (error: any) {
      setChannelsError(error.message || 'Failed to fetch channels from remote gateway');
    } finally {
      setLoadingChannels(false);
    }
  };

  const fetchAllChannels = async () => {
    try {
      const response = await apiClient.get('/channels');
      setAllChannels(response.data.filter((ch: any) => ch.status === 'active'));
    } catch (error: any) {
      console.error('Failed to load channels:', error);
    }
  };

  const fetchGatewayIntegrations = async () => {
    if (!id) return;
    try {
      const response = await apiClient.get(`/gateways/${id}/integrations`);
      setForm(prev => ({
        ...prev,
        integrations: response.data.integration_integrations || []
      }));
      setIntegrationsModified(false);
    } catch (error: any) {
      console.error('Failed to load gateway integrations:', error);
      // Don't show error to user, just keep empty integrations
    }
  };

  const handleToggleExposedChannel = (channelId: string) => {
    const exposed = form.exposed_channels || [];
    const isCurrentlyExposed = exposed.includes(channelId);
    setForm({
      ...form,
      exposed_channels: isCurrentlyExposed
        ? exposed.filter(id => id !== channelId)
        : [...exposed, channelId]
    });
  };

  const handleSaveExposedChannels = async () => {
    if (!id) return;
    
    try {
      setSavingExposedChannels(true);
      await apiClient.put(`/gateways/${id}/exposed-channels`, {
        exposed_channels: form.exposed_channels || []
      });
      setSuccess('Exposed channels updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError(error.message || 'Failed to update exposed channels');
    } finally {
      setSavingExposedChannels(false);
    }
  };

  const handleIntegrationsChange = (integrations: integrationIntegration[]) => {
    setForm({ ...form, integrations });
    setIntegrationsModified(true);
  };

  const handleSaveIntegrations = async () => {
    if (!id) {
      console.error('No gateway ID available for saving integrations');
      return;
    }
    try {
      setSavingIntegrations(true);
      const payload = {
        integration_integrations: form.integrations || []
      };
      const url = `/gateways/${id}/integrations`;
      const response = await apiClient.put(url, payload);
      setSuccess('Gateway integrations updated successfully!');
      setIntegrationsModified(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Failed to save integrations:', error);
      console.error('Error details:', error.message, error.stack);
      setError(error.message || 'Failed to update gateway integrations');
    } finally {
      setSavingIntegrations(false);
    }
  };

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(null);

    if (!form.name || !form.did) {
      setError('Name and DID are required');
      return;
    }

    try {
      setSaving(true);
      const gatewayData = {
        name: form.name,
        description: form.description,
        did: form.did,
        gateway_type: form.gateway_type,
        status: form.status
      };
      if (isEditMode) {
        await apiClient.put(`/gateways/${id}`, gatewayData);
        setSuccess('Gateway updated successfully!');
      } else {
        await apiClient.post('/gateways', form);
        setSuccess('Gateway created successfully!');
        // Only navigate after creation, not after update
        setTimeout(() => navigate('/gateways'), 1000);
      }
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError(error.message || 'Failed to save gateway');
    } finally {
      setSaving(false);
    }
  }, [form, isEditMode, id, navigate, hasIntegrationErrors]);

  const handleDelete = async () => {

    try {
      setSaving(true);
      await apiClient.delete(`/gateways/${id}`);
      navigate('/gateways');
    } catch (error: any) {
      setError(error.message || 'Failed to delete gateway');
    } finally {
      setSaving(false);
    }
  };

  // Add keyboard shortcut support for save (Ctrl+S / Cmd+S)
  useEffect(() => {
    const handleKeyboardSave = (event: KeyboardEvent) => {
      // Check for Ctrl+S (Windows/Linux) or Cmd+S (Mac)
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault(); // Prevent browser's default save behavior
        
        // Only trigger save if not currently saving and form is valid
        if (!saving && form.name && form.did) {
          handleSubmit(event as any);
        }
      }
    };

    // Add event listener when component mounts
    document.addEventListener('keydown', handleKeyboardSave);

    // Cleanup: remove event listener when component unmounts
    return () => {
      document.removeEventListener('keydown', handleKeyboardSave);
    };
  }, [saving, form.name, form.did, handleSubmit]);

  if (id === 'self' || loading) {
    return (
      <div className="container mt-4">
        <div className="text-center py-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden"></span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {error && (
        <div 
          key={`error-${Date.now()}`}
          className="alert alert-danger alert-dismissible fade show"
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 9999,
            minWidth: '400px',
            fontSize: '14px',
            fontWeight: 'bold',
            border: '2px solid #dc3545',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}
        >
          <i className="fas fa-exclamation-triangle mr-2"></i>
          <strong>Error!</strong> {error}
          <button type="button" className="close" onClick={() => setError('')} title="Close notification">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      )}

      {success && (
        <div 
          key={`success-${Date.now()}`}
          className="alert alert-success alert-dismissible fade show"
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 9999,
            minWidth: '400px',
            fontSize: '14px',
            fontWeight: 'bold',
            border: '2px solid #28a745',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}
        >
          <i className="fas fa-check-circle mr-2"></i>
          <strong>Success!</strong> {success}
          <button type="button" className="close" onClick={() => setSuccess(null)} title="Close notification">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      )}
      
      <div className="card shadow mb-4 channel-editor-card">
        <div className="card-header py-3 d-flex justify-content-between align-items-center">
          <h6 className="m-0 font-weight-bold text-primary">
            <i className={`fas fa-${isEditMode ? 'edit' : 'plus'}`}></i> {isEditMode ? `Edit Gateway: ${form.name}` : 'Add New Gateway'}
            {form.status === 'disabled' && (
              <span className="badge badge-warning ml-2">
                <i className="fas fa-power-off"></i> DISABLED
              </span>
            )}
          </h6>
          <div>
            {isEditMode && (
              <button 
                className={`btn btn-sm btn-primary mr-2 ${saving ? 'disabled' : ''}`}
                onClick={handleSubmit}
                disabled={saving}
              >
                <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-save'}`}></i> 
                {saving ? ' Saving...' : ' Save'}
              </button>
            )}
            {isEditMode && !isSelfGateway && (
              <button 
                className={`btn btn-sm btn-outline-danger mr-2 ${saving ? 'disabled' : ''}`}
                onClick={handleDelete}
                disabled={saving}
                title="Delete this gateway"
              >
                <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-trash'}`}></i> 
                {saving ? ' Deleting...' : ' Delete'}
              </button>
            )}
            <button className="btn btn-sm btn-secondary" onClick={() => navigate('/gateways')}>
              <i className="fas fa-times"></i> Close
            </button>
          </div>
        </div>
        <div className="card-body-channel-editor">
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k || 'overview')}
            className="mb-3 custom-channel-tabs"
          >
            {/* Overview Tab */}
            <Tab eventKey="overview" title={<><i className="fas fa-info-circle"></i> Overview</>}>
              <OverviewTab
                form={form}
                setForm={setForm}
                isEditMode={isEditMode}
                isSelfGateway={isSelfGateway}
                saving={saving}
                id={id}
                handleSubmit={handleSubmit}
                handleDelete={handleDelete}
                onNavigate={() => navigate('/gateways')}
              />
            </Tab>

            {/* Publishing Tab - Only for remote gateways in edit mode */}
            {isEditMode && form.gateway_type === 'remote' && (
              <Tab eventKey="publishing" title={<><i className="fas fa-filter"></i> Publishing</>}>
                <PublishingTab
                  form={form}
                  setForm={setForm}
                  allChannels={allChannels}
                  savingExposedChannels={savingExposedChannels}
                  success={success}
                  setSuccess={setSuccess}
                  handleToggleExposedChannel={handleToggleExposedChannel}
                  handleSaveExposedChannels={handleSaveExposedChannels}
                />
              </Tab>
            )}

            {/* Remote Tab - Only for remote gateways in edit mode */}
            {isEditMode && form.gateway_type === 'remote' && (
              <Tab eventKey="remote" title={<><i className="fas fa-network-wired"></i> Remote</>}>
                <RemoteTab
                  remoteChannels={remoteChannels}
                  loadingChannels={loadingChannels}
                  channelsError={channelsError}
                  fetchRemoteChannels={fetchRemoteChannels}
                />
              </Tab>
            )}

            {/* Integrations Tab - Only in edit mode */}
            {isEditMode && (
              <Tab eventKey="integrations" title={<><i className="fas fa-plug"></i> Integrations</>}>
                <GatewayIntegrationsTab
                  integrations={form.integrations || []}
                  onIntegrationsChange={handleIntegrationsChange}
                  onValidationChange={setHasIntegrationErrors}
                  onSave={handleSaveIntegrations}
                  isModified={integrationsModified}
                  isSaving={savingIntegrations}
                />
              </Tab>
            )}
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default EditGatewayPage;
